import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const KEY = "rfh-intro-seen";
const HOLD_MS = 1250;
const EASE_OUT = [0.16, 1, 0.3, 1];
const EASE_CURTAIN = [0.76, 0, 0.24, 1];

/**
 * A short ink curtain with the wordmark, shown once per session before the first screen. It lifts
 * after ~1.2 s and calls `onDone` so the hero can start its own reveal the moment the photo is
 * uncovered. Skipped for prefers-reduced-motion and on repeat visits in the same tab.
 */
export const Intro = ({ onDone }) => {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => {
    if (reduce || typeof window === "undefined") return false;
    try { return !window.sessionStorage.getItem(KEY); } catch { return false; }
  });

  useEffect(() => {
    if (!show) { onDone?.(); return undefined; }
    try { window.sessionStorage.setItem(KEY, "1"); } catch { /* private mode */ }
    const t = setTimeout(() => setShow(false), HOLD_MS);
    return () => clearTimeout(t);
  }, [show, onDone]);

  return (
    <AnimatePresence onExitComplete={() => onDone?.()}>
      {show && (
        <motion.div
          key="intro"
          className="fixed inset-0 z-[70] flex flex-col items-start justify-end bg-fjord px-5 pb-10 text-white sm:px-8 lg:px-10 lg:pb-14"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.85, ease: EASE_CURTAIN }}
          aria-hidden
          data-testid="home-intro"
        >
          <div className="mx-auto w-full max-w-site 2xl:max-w-[1400px]">
            <div className="overflow-hidden">
              <motion.p className="label-caps text-white/60" initial={{ y: "120%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE_OUT }}>
                Rosendal · Hardangerfjorden
              </motion.p>
            </div>
            <div className="mt-3 overflow-hidden pb-[0.12em]">
              <motion.p className="text-[clamp(2.25rem,6vw,5.5rem)] font-medium leading-[1] tracking-[-0.035em]" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.18, ease: EASE_OUT }}>
                Rosendal Fjordhotel
              </motion.p>
            </div>
            <motion.div className="mt-8 h-px w-full origin-left bg-white/25" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.1, delay: 0.25, ease: EASE_OUT }} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
